/**
 * Tool dispatch for the operational request bus (staff requests,
 * transportation, schedule, menu) - split out of useRealtimeVoice.js
 * 2026-08-10, which had grown well past the repo's 400-line code-file
 * cap. Same dispatch contract as the parent's executeTool(): returns
 * `{ ok, message, ... }` for names it handles, or `undefined` for
 * anything else so the caller can fall through to its own dispatch.
 */
import { API } from "./api";
import { transportStatusMessage, rideBookedMessage } from "./transportation";
import { menuToolMessage, scheduleToolMessage, ownPlanLine } from "./communityServices";
import { literalResidentWords, newRequestMessage, openRequestsMessage } from "./requestOverview";
import { cleanItems } from "./requestItems";

// 2026-08-23: the backend rejects an operational mutation (422 +
// needs_clarification) when a free-text field claims a fact - so far only
// a clock time - the resident never actually stated this session (Room
// 304's fabricated "10 o'clock" reaching a real staff task). Surface that
// as a natural spoken question instead of the generic "couldn't send that
// request" failure, so the model asks rather than silently retrying with
// the same invented detail.
async function needsClarificationMessage(r) {
  if (r.status !== 422) return null;
  try {
    const body = await r.json();
    const reason = body?.detail?.reason;
    // A backend-written question (e.g. a past date) is asked as-is.
    if (body?.detail?.needs_clarification && body.detail.ask) return `${body.detail.ask}`;
    if (body?.detail?.needs_clarification && reason) {
      return `I don't have that confirmed - ${reason}. Could you tell me the actual time?`;
    }
  } catch {}
  return null;
}


// "asked N times" counts the first ask plus every re-request - the same count
// the staff queue shows. Backend sends times_asked; re_request_count alone is
// only the number of repeats.
function timesAsked(d) {
  return d.times_asked || (Number(d.re_request_count) || 0) + 1;
}

export async function executeOperationsTool({ name, args, ctx }) {
  const { room, residentId, sessionId, turnSuspect, turnSuspectReason } = ctx;
  // 2026-08-23: "echo_like" genuinely suggests mishearing - ask to repeat.
  // Other suspect reasons read better as a quick confirmation, not "I
  // misheard you" - see realtimeMessageHandler.js's classifyUserTurn().
  const suspectMsg = turnSuspectReason === "echo_like"
    ? "I want to make sure I heard that right — could you say that again?"
    : "Just to double-check — is that what you'd like me to do?";

  if (name === "request_staff_help" && turnSuspect) {
    // The turn that triggered this looked like it might not be genuine
    // resident speech (echo/noise-hallucination signal) - don't file a
    // real staff ticket over words nobody may have actually said.
    return { ok: false, message: suspectMsg };
  }

  if (name === "request_staff_help") {
    const r = await fetch(`${API}/tasks/resident-request`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        category: args.category,
        resident_id: residentId || null,
        room: room || null,
        resident_words: literalResidentWords(ctx),  // the resident's own sentence, not the model's summary
        summary: args.summary || "Resident request",
        priority: args.priority || "normal",
        source: "aria_voice",
        conversation_session_id: sessionId || null,
        items: cleanItems(args.items),   // supplies/linens: what and how many the resident asked for
      }),
    });
    if (!r.ok) {
      const clarify = await needsClarificationMessage(r);
      return { ok: false, message: clarify || `couldn't send that request (${r.status}) - please try the call button instead.` };
    }
    const data = await r.json();
    if (data.duplicate) {
      // 2026-08-27: the existing open ticket may be about a DIFFERENT
      // problem than what was just asked (same category, different issue) -
      // say what it's actually about instead of implying it's the same one.
      const aboutClause = data.same_issue || !data.existing_summary
        ? "this"
        : `something else already open in ${args.category} ("${data.existing_summary}")`;
      return {
        ok: true,
        message: `there's already an open ${args.category} request on file for ${aboutClause} - I've let them know again (they've now been asked ${timesAsked(data)} times).${data.spoken ? ` ${data.spoken}` : ""}`,
        task_id: data.task_id,
      };
    }
    return { ok: true, message: newRequestMessage(args.category, data.delivery), task_id: data.task_id };
  }

  if (name === "check_request_status" || name === "check_request_history") {
    const history = name === "check_request_history";
    const qs = new URLSearchParams();
    if (residentId) qs.set("resident_id", residentId);
    else if (room) qs.set("room", room);
    else qs.set("conversation_session_id", sessionId || "");
    if (args.category) qs.set("category", args.category);
    if (!history && !args.category) {
      // No category = "any of my requests": every open one, each with its own state.
      const oq = new URLSearchParams(qs);
      oq.set("exclude_category", "transportation");
      const [ro, rr] = await Promise.all([
        fetch(`${API}/tasks/resident-request/open?${oq.toString()}`),
        fetch(`${API}/transportation/request/status?${qs.toString()}`),
      ]);
      if (!ro.ok) return { ok: false, message: `couldn't check that (${ro.status}).` };
      return { ok: true, message: openRequestsMessage(await ro.json(), rr.ok ? await rr.json() : null) };
    }
    if (!history && args.category) {
      // More than one open request in this department (e.g. two housekeeping jobs):
      // read them all, each with its own state and plan, instead of only the newest.
      const cq = new URLSearchParams(qs);
      const rc = await fetch(`${API}/tasks/resident-request/open?${cq.toString()}`).catch(() => null);
      const many = rc && rc.ok ? await rc.json().catch(() => null) : null;
      if (many?.requests?.length > 1) return { ok: true, message: openRequestsMessage(many, null) };
    }
    const path = history ? "history" : "status";
    const r = await fetch(`${API}/tasks/resident-request/${path}?${qs.toString()}`);
    if (!r.ok) return { ok: false, message: `couldn't check that (${r.status}).` };
    const data = await r.json();

    const lifecycle = (d) => {
      const seg = [];
      if (d.created?.label) seg.push(`asked ${d.created.label}`);
      if (d.last_re_requested_at?.label) seg.push(`asked again ${d.last_re_requested_at.label}`);
      if (d.acknowledged_at?.label) seg.push(`acknowledged ${d.acknowledged_at.label}`);
      if (d.started_at?.label) seg.push(`started ${d.started_at.label}`);
      if (d.completed_at?.label) seg.push(`completed ${d.completed_at.label}`);
      return seg;
    };

    if (history) {
      if (!data.found || !data.requests?.length) {
        return { ok: true, message: "nothing finished on record for that." };
      }
      const items = data.requests.slice(0, 3).map((d) => {
        const t = lifecycle(d);
        const said = d.spoken ? ` ${d.spoken.replace(/\.$/, "")}` : "";
        return `${d.what_for || "a request"} (${d.category})${t.length ? " — " + t.join(", ") : ""}.${said}`;
      });
      return { ok: true, message: "past requests: " + items.join("; ") + "." };
    }

    // current
    if (!data.found) {
      return { ok: true, message: "you have no open request on record right now." };
    }
    const scheduleClause = data.scheduled_date || data.scheduled_time_label
      ? `planned for ${[data.scheduled_time_label, data.scheduled_date].filter(Boolean).join(" on ")}`
      : "no scheduled time yet";
    // `spoken` is the backend's authoritative status (same lifecycle as
    // Aria's "right now" context: owner, latest staff note with its real
    // time). Do not restate raw status/ownership/notes here - a second
    // wording is what contradicted it before (SC-3/SC-5).
    const parts = [
      ...(data.spoken ? [`summary: ${data.spoken.replace(/\.$/, "")}`] : []),
      `it's for ${data.what_for || "something you asked about"}`,
      scheduleClause,
    ];
    const t = lifecycle(data);
    if (t.length) parts.push(t.join(", "));
    if (data.times_asked > 1) parts.push(`asked ${data.times_asked} times in all`);
    return { ok: true, message: parts.join("; ") + "." };
  }

  if (name === "check_transportation_availability") {
    // Resource-aware (driver+vehicle) - the same engine request_transportation
    // actually books against, so this can never promise an opening the
    // booking call then can't honor.
    const qs = args.date ? `?date=${encodeURIComponent(args.date)}` : "";
    const r = await fetch(`${API}/transportation/availability/public${qs}`);
    if (!r.ok) return { ok: false, message: `couldn't check availability (${r.status}).` };
    const slots = await r.json();
    const open = slots.filter((s) => s.open);
    if (!open.length) return { ok: true, message: "no open transportation times found for that date." };
    return { ok: true, message: `open times: ${open.map((s) => s.start_time).join(", ")}.` };
  }

  if (name === "request_transportation" && turnSuspect) {
    return { ok: false, message: suspectMsg };
  }

  if (name === "request_transportation") {
    const r = await fetch(`${API}/transportation/request`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        resident_id: residentId || null,
        room: room || null,
        purpose: args.purpose,
        requested_for_date: args.requested_for_date,
        requested_for_time_label: args.requested_for_time_label || null,
        start_time: args.start_time || null,
        source: "aria_voice",
        conversation_session_id: sessionId || null,
        resident_words: literalResidentWords(ctx),  // N3: literal utterance, separate from purpose
      }),
    });
    if (!r.ok) {
      const clarify = await needsClarificationMessage(r);
      return { ok: false, message: clarify || `couldn't send that request (${r.status}) - please try the call button instead.` };
    }
    const data = await r.json();
    if (data.duplicate) {
      return { ok: true, message: `there's already an open transportation request on file for ${data.status === "pending" ? "that date" : data.status} - I've flagged it again (they've now been asked ${timesAsked(data)} times).` };
    }
    if (!data.booked) {
      return { ok: true, message: `request submitted for ${args.requested_for_date} - the front desk needs to coordinate the time, no confirmed time yet.` };
    }
    return { ok: true, message: rideBookedMessage(data, { askedTime: args.start_time }) };
  }

  if (name === "check_transportation_status") {
    const qs = new URLSearchParams();
    if (residentId) qs.set("resident_id", residentId);
    else if (room) qs.set("room", room);
    else qs.set("conversation_session_id", sessionId || "");
    const r = await fetch(`${API}/transportation/request/status?${qs.toString()}`);
    if (!r.ok) return { ok: false, message: `couldn't check that (${r.status}).` };
    const data = await r.json();
    return { ok: true, message: transportStatusMessage(data) };
  }

  if (name === "change_transportation_request") {
    const r = await fetch(`${API}/transportation/request/change-mine`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        resident_id: residentId || null,
        room: room || null,
        conversation_session_id: sessionId || null,
        requested_for_date: args.requested_for_date,
        requested_for_time_label: args.requested_for_time_label || null,
        start_time: args.start_time || null,
      }),
    });
    if (!r.ok) return { ok: false, message: `couldn't change that (${r.status}).` };
    const data = await r.json();
    return {
      ok: true,
      message: data.booked
        ? rideBookedMessage(data, { askedTime: args.start_time, changed: true })
        : `changed to ${args.requested_for_date} - no confirmed time yet.`,
    };
  }

  if (name === "cancel_transportation_request") {
    const r = await fetch(`${API}/transportation/request/cancel-mine`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ resident_id: residentId || null, room: room || null, conversation_session_id: sessionId || null }),
    });
    if (!r.ok) return { ok: false, message: `couldn't cancel that (${r.status}).` };
    const cancelled = await r.json().catch(() => ({}));
    if (cancelled.already_cancelled) return { ok: true, message: "that ride is already cancelled." };
    return { ok: true, message: "cancelled." };
  }

  if (name === "get_todays_schedule") {
    const qs = new URLSearchParams();
    if (args.date) qs.set("date", args.date);
    const r = await fetch(`${API}/schedule/public/today?${qs.toString()}`);
    if (!r.ok) return { ok: false, message: `couldn't reach the schedule (${r.status}).` };
    let own = "";
    if (residentId || room) {   // the resident's own planned appointments that day, from their real plan
      const uq = new URLSearchParams(residentId ? { resident_id: residentId } : { room });
      uq.set("days", "14");
      const ru = await fetch(`${API}/tasks/resident-request/upcoming?${uq.toString()}`).catch(() => null);
      if (ru && ru.ok) own = ownPlanLine(await ru.json().catch(() => null), args.date || null);
    }
    return { ok: true, message: scheduleToolMessage(await r.json(), { date: args.date || null }) + own };
  }

  if (name === "get_menu") {
    const qs = new URLSearchParams();
    if (args.meal_period) qs.set("meal_period", args.meal_period);
    if (args.date) qs.set("date", args.date);
    const r = await fetch(`${API}/menu/public/today?${qs.toString()}`);
    if (!r.ok) return { ok: false, message: `couldn't reach the menu (${r.status}).` };
    const items = await r.json();
    return { ok: true, message: menuToolMessage(items, { mealPeriod: args.meal_period || null, date: args.date || null }) };
  }

  if (name === "confirm_interpretation_pattern") {
    // Terminal 10 (person-specific interpretation continuity) - resident-
    // scoped only; silently no-ops without a resident_id rather than
    // filing a pattern nobody can be scoped to later.
    if (!residentId) return { ok: true, message: "noted for this call." };
    const r = await fetch(`${API}/aria/interpretation-patterns/confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        resident_id: residentId, session_id: sessionId || "",
        heard_as: args.heard_as, understood_as: args.understood_as,
        meaning: args.meaning || null, language: args.language || null,
        category: args.category || null,
      }),
    });
    if (!r.ok) return { ok: true, message: "noted for this call." };
    return { ok: true, message: "got it — I'll remember that." };
  }

  return undefined;
}
